import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useEffect, useState } from 'react';
import { cloneDeep, isEqual, startCase, toLower } from 'lodash';
import { Select, MenuItem, FormControlLabel, Checkbox, FormControl, Button, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { Tooltip } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { datadogLogs } from '@datadog/browser-logs';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../../utils/styleHelpers';
import { bulkCreateLinks, createLink, Link, LinkStatus } from '../../../../store/Link';
import { useSnackbar } from '../../../../Context/SnackbarContext';
import { TextBlock, TextSpan, Text } from '../../../Atoms/Text';
import { SearchBox } from '../../Search/Search';
import { fetchRpmAccountsByAsset, fetchRpmAccountsByPAsset, RpmAccount } from '../../../../store/RpmAccount';
import { Asset, fetchAsset, fetchAssetsByRpmAcctId } from '../../../../store/Asset';
import {
  ProvisionalAsset,
  fetchProvisionalAsset,
  fetchProvisionalAssetsByRpmAcctId
} from '../../../../store/ProvisionalAsset';
import { EntityType, SearchResult, TypeAsset } from '../../../../store/Search';
import { assetCommissionsStatusOptions } from '../../../../store/GeneralStore';
import { Icon } from '../../../Atoms/Icon';
import { getCurrentUser } from '../../../../store/User';
import { ComponentSpinner } from '../../Loading/ComponentSpinner';
import { getAsset, patchAsset } from '../../../../Api/Asset/AssetApi';
import { getProvisionalAsset, patchProvisionalAsset } from '../../../../Api/ProvisionalAsset/ProvisionalAssetApi';
import { getRpmAccount, patchRpmAccount } from '../../../../Api/RpmAccount/RpmAccountApi';
import { getUTCRunDate, getFormattedDate, downloadCsvFromData } from '../../../../utils/helpers';
import { CustomActionBar } from '../../../Compounds/DetailPanel/CustomActionBar';
import { BulkRPMLinkCreateResults } from '../../../../Api/Link/LinkApi';
import { getCommissionRunInfoByAccounts } from '../../../../Api/RpmAccount/RpmAccountApi';
import { RecordLink } from '../../../Atoms/RecordLink';

const salesforceAssetUrl = `${process.env.REACT_APP_SALESFORCE_INSTANCE_URL}/lightning/r/Asset`;

const downloadBulkRPMLinkCreateResultsCsv = (bulkRPMLinkResults: BulkRPMLinkCreateResults | null) => {
  let csvData = 'RPM Account ID,Result\n';
  if (bulkRPMLinkResults) {
    csvData += bulkRPMLinkResults?.success.map((link) => `${link.rpmAccountId},new link\n`).join('');
    csvData += bulkRPMLinkResults?.activated.map((link) => `${link.rpmAccountId},existing link activated\n`).join('');
    csvData += bulkRPMLinkResults?.duplicate.map((link) => `${link.rpmAccountId},existing active link\n`).join('');
    csvData += bulkRPMLinkResults?.missingRpmAccount.map((link) => `${link.rpmAccountId},ID not found\n`).join('');
  }
  downloadCsvFromData(csvData, 'BulkLinkCreationResults.csv');
};

export interface NewLinkDialogParams {
  open: boolean;
  setOpen: (state: boolean) => void;
  asset?: Asset | ProvisionalAsset;
  rpmAccount?: RpmAccount;
  typeAsset?: TypeAsset;
  drawer?: boolean;
}

interface CommissionRunInfo {
  firstCommissionRun: string | null;
  lastCommissionRun: string | null;
}

interface FormData {
  setCommissionStatus: boolean;
  commissionStatus: string;
  setActualFirstRun: boolean;
  actualFirstRun: Date | null;
  setRepAssign: boolean;
  repAssignName: string;
  repAssignId: number | null;
  setParentCommissionStatus: boolean;
  parentCommissionStatus: string;
  setActualDecommissionRun: boolean;
  actualDecommissionRun: Date | null;
}

const defaultFormData: FormData = {
  setCommissionStatus: false,
  commissionStatus: 'Commissioning',
  setActualFirstRun: false,
  actualFirstRun: null,
  setRepAssign: false,
  repAssignName: '',
  repAssignId: null,
  setParentCommissionStatus: false,
  parentCommissionStatus: 'Decommissioned',
  setActualDecommissionRun: false,
  actualDecommissionRun: null
};

export const NewLinkDialog = ({
  open,
  setOpen,
  asset,
  rpmAccount,
  typeAsset = EntityType.ASSET,
  drawer = false
}: NewLinkDialogParams) => {
  const { setSnack } = useSnackbar();
  const [newSfdcUpstackId, setNewSfdcUpstackId] = useState('');
  const [sourceObject, setSourceObject] = useState<Asset | ProvisionalAsset | RpmAccount | null>(null);
  const [assetParent, setAssetParent] = useState<Asset | null>(null);
  const [newRpmAccountId, setNewRpmAccountId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showBulkRPMResults, setShowBulkRPMResults] = useState(false);
  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const [isBulkRPM, setIsBulkRPM] = useState(false);
  const [rpmAccountIds, setRpmAccountIds] = useState<string[]>([]);
  const [bulkRPMLinkResults, setBulkRPMLinkResults] = useState<BulkRPMLinkCreateResults | null>(null);
  const [commissionRunInfo, setCommissionRunInfo] = useState<CommissionRunInfo | null>(null);
  const user = getCurrentUser();
  const isAssetType = typeAsset === EntityType.ASSET;
  const fetchByRpmAcctId = isAssetType ? fetchAssetsByRpmAcctId : fetchProvisionalAssetsByRpmAcctId;
  const iconAsset = isAssetType ? 'assetSmall' : 'provisionalAssetSm';

  const assetData = (asset || sourceObject) as Asset | ProvisionalAsset;
  const rpmAccountData = (rpmAccount || sourceObject) as RpmAccount;

  useEffect(() => {
    setNewSfdcUpstackId(asset?.id ? asset.id : '');
    setNewRpmAccountId(rpmAccount?.accountId ? rpmAccount?.accountId : '');
    if (!open) {
      setSourceObject(null);
      setFormData(defaultFormData);
      setAssetParent(null);
      setIsBulkRPM(false);
      setRpmAccountIds([]);
      setBulkRPMLinkResults(null);
      setShowBulkRPMResults(false);
    }
  }, [open, asset, rpmAccount]);

  useEffect(() => {
    if (isBulkRPM) {
      setNewRpmAccountId('');
      setFormData((prev) => ({
        ...prev,
        setRepAssign: false,
        repAssignName: '',
        repAssignId: null
      }));
    }
  }, [isBulkRPM]);

  useEffect(() => {
    const commissionStatus = assetData?.id
      ? assetData[(isAssetType ? 'commissionsStatus' : 'commissionStatus') as keyof typeof assetData]
      : '';
    setFormData((prev) => ({
      ...prev,
      setCommissionStatus: assetData?.id ? commissionStatus === 'Pending Commissions' : false,
      commissionStatus: 'Commissioning',
      setActualFirstRun: assetData?.id ? !assetData?.actualFirstCommissionRun : false,
      actualFirstRun: assetData?.actualFirstCommissionRun ? getUTCRunDate(assetData.actualFirstCommissionRun) : null
    }));
    if (assetData?.id && isAssetType) {
      const replacementOfAssetID = (assetData as Asset).replacementOfAssetID;
      if (replacementOfAssetID) {
        fetchSourceObject(getAsset, replacementOfAssetID, true);
      } else {
        setAssetParent(null);
      }
    } else {
      setAssetParent(null);
    }
  }, [open, assetData]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      setRepAssign: rpmAccountData?.accountId ? !rpmAccountData?.assignedRep : false,
      repAssignName: '',
      repAssignId: null
    }));
  }, [rpmAccountData]);

  useEffect(() => {
    const commissionStatus = assetParent?.id ? assetParent.commissionsStatus : '';
    const runByFirst =
      assetParent?.id && assetData?.actualFirstCommissionRun ? getUTCRunDate(assetData.actualFirstCommissionRun) : null;
    if (runByFirst) runByFirst.setMonth(runByFirst.getMonth() - 1);
    setFormData((prev) => ({
      ...prev,
      setParentCommissionStatus: assetParent?.id ? commissionStatus !== 'Decommissioned' : false,
      parentCommissionStatus: 'Decommissioned',
      setActualDecommissionRun: assetParent?.id ? !assetParent.actualDecommissionedDate : false,
      actualDecommissionRun:
        assetParent?.id && assetParent?.actualDecommissionedDate
          ? getUTCRunDate(assetParent.actualDecommissionedDate)
          : runByFirst
    }));
  }, [assetParent]);

  const fetchSourceObject = async (
    fetchFn: (id: string) => Promise<{ data?: any; error?: any }>, // eslint-disable-line @typescript-eslint/no-explicit-any
    id: string,
    isParent = false
  ) => {
    const { data, error } = await fetchFn(id);
    if (data) {
      const setFn = isParent ? setAssetParent : setSourceObject;
      setFn(data);
    } else if (error) {
      setSnack({ message: `Error fetching source object: ${error.message}`, type: 'error', open: true });
    }
  };

  useEffect(() => {
    if (newSfdcUpstackId && !asset) {
      const fetchAssetFn = isAssetType ? getAsset : getProvisionalAsset;
      fetchSourceObject(fetchAssetFn, newSfdcUpstackId);
    } else if (newRpmAccountId && !rpmAccount) {
      fetchSourceObject(getRpmAccount, newRpmAccountId);
    } else {
      setSourceObject(null);
    }
  }, [newSfdcUpstackId, newRpmAccountId]);

  const fetchCommissionRunInfoByAccounts = async () => {
    const cleanedRpmAccountIds = rpmAccountIds.filter((id) => id.trim() !== '').map((id) => Number(id));
    if (cleanedRpmAccountIds.length === 0) {
      setCommissionRunInfo(null);
      return null;
    }

    const { data, error } = await getCommissionRunInfoByAccounts(cleanedRpmAccountIds);
    if (data) {
      setCommissionRunInfo({
        firstCommissionRun: data.firstCommissionRun,
        lastCommissionRun: data.lastCommissionRun
      });
    } else if (error) {
      setCommissionRunInfo(null);
      setSnack({ message: `Error fetching commission run info: ${error.message}`, type: 'error', open: true });
    }
  };

  useEffect(() => {
    if (rpmAccount) {
      setCommissionRunInfo({
        firstCommissionRun: rpmAccount.firstCommissionRun,
        lastCommissionRun: rpmAccount.lastCommissionRun
      });
    } else if (!isBulkRPM && sourceObject && 'firstCommissionRun' in sourceObject) {
      setCommissionRunInfo({
        firstCommissionRun: sourceObject.firstCommissionRun,
        lastCommissionRun: sourceObject.lastCommissionRun
      });
    } else if (isBulkRPM && rpmAccountIds.length > 0) {
      fetchCommissionRunInfoByAccounts();
    } else {
      setCommissionRunInfo(null);
    }
  }, [rpmAccount, sourceObject, isBulkRPM, rpmAccountIds]);

  const handleClose = () => {
    if (!isSubmitting) setOpen(false);
  };

  const handleSelect = (result: SearchResult) => {
    if (result.source === 'asset' || result.source === 'provisional_asset') {
      setNewSfdcUpstackId(result.id);
    } else if (result.source === 'rpm_account') {
      setNewRpmAccountId(result.id);
    }
  };

  const handleClearSelect = () => {
    const setFn = asset ? setNewRpmAccountId : setNewSfdcUpstackId;
    setFn('');
    setSourceObject(null);
  };

  async function updateObject<T>(
    original: T | null | undefined,
    updates: (draft: T) => void,
    patchFn: (obj: T) => void,
    typeName?: string
  ) {
    if (!original) return;
    const updated = cloneDeep(original);
    updates(updated);

    if (isEqual(updated, original)) return;
    datadogLogs.logger.info('Object updated', { type: typeName, original, updated });
    patchFn(updated);
  }

  const updateObjectByFormData = async () => {
    await updateObject<Asset>(
      assetParent,
      (draft) => {
        if (formData.setParentCommissionStatus) {
          draft.commissionsStatus = formData.parentCommissionStatus;
        }
        if (formData.setActualDecommissionRun && formData.actualDecommissionRun) {
          draft.actualDecommissionedDate = getUTCRunDate(formData.actualDecommissionRun).toISOString();
        }
      },
      patchAsset,
      'Asset'
    );

    if (isAssetType) {
      await updateObject<Asset>(
        assetData as Asset,
        (draft) => {
          if (formData.setCommissionStatus) {
            draft.commissionsStatus = formData.commissionStatus;
          }
          if (formData.setActualFirstRun && formData.actualFirstRun) {
            draft.actualFirstCommissionRun = getUTCRunDate(formData.actualFirstRun).toISOString();
          }
        },
        patchAsset,
        'Asset'
      );
    } else {
      await updateObject<ProvisionalAsset>(
        assetData as ProvisionalAsset,
        (draft) => {
          if (formData.setCommissionStatus) {
            draft.commissionStatus = formData.commissionStatus;
          }
          if (formData.setActualFirstRun && formData.actualFirstRun) {
            draft.actualFirstCommissionRun = getUTCRunDate(formData.actualFirstRun).toISOString();
          }
        },
        patchProvisionalAsset,
        'ProvisionalAsset'
      );
    }

    await updateObject<RpmAccount>(
      rpmAccountData,
      (draft) => {
        if (formData.setRepAssign) {
          draft.assignedRep = formData.repAssignName;
          draft.assignedRepId = formData.repAssignId;
        }
      },
      patchRpmAccount,
      'RpmAccount'
    );
  };

  const handleSubmitForm = async (isAddNew = false) => {
    if (user) {
      setIsSubmitting(true);

      // Remove empty rpmAccountIds and duplicates
      const cleanedRpmAccountIds = Array.from(new Set(rpmAccountIds.filter((id) => id.trim() !== '')));
      setRpmAccountIds(cleanedRpmAccountIds);

      if (isBulkRPM) {
        const links: Partial<Link>[] = cleanedRpmAccountIds
          .filter((id) => id.trim() !== '')
          .map((rpmAccountId) => ({
            rpmAccountId: Number(rpmAccountId),
            sfdcUpstackId: newSfdcUpstackId,
            status: LinkStatus.ACTIVE,
            createdBy: user,
            linkEvents: []
          }));
        datadogLogs.logger.info('Creating bulk links', { links });
        const { data: results, error } = await bulkCreateLinks(links);
        if (error) {
          throw new Error(`There was an error creating links: \n${error.message}`);
        }
        setBulkRPMLinkResults(results);
        await updateObjectByFormData();
        setShowBulkRPMResults(true);
        setIsSubmitting(false);
      } else {
        const newLink: Link = {
          rpmAccountId: Number(newRpmAccountId),
          sfdcUpstackId: newSfdcUpstackId,
          status: LinkStatus.ACTIVE,
          createdBy: user,
          linkEvents: [],
          repeatingOverrideEnabled: false,
          repeatingOverridePercent: null,
          repeatingOverrideModifiedAt: null
        };
        datadogLogs.logger.info('Creating new link', { newLink });
        const { data, error } = await createLink(newLink);
        if (data) {
          await updateObjectByFormData();
          setSnack({ message: 'Link was created successfully', type: 'success', open: true });
          if (asset?.id) {
            if (isAssetType) {
              fetchRpmAccountsByAsset(newSfdcUpstackId, drawer)();
              fetchAsset(asset.id, true);
            } else {
              fetchRpmAccountsByPAsset(asset.id, drawer)();
              fetchProvisionalAsset(asset.id, true);
            }
          } else {
            fetchByRpmAcctId(newRpmAccountId, drawer)();
          }
          setIsSubmitting(false);
          if (isAddNew) {
            setNewSfdcUpstackId(asset?.id ? asset.id : '');
            setNewRpmAccountId(rpmAccount?.accountId ? rpmAccount?.accountId : '');
          } else {
            handleClose();
          }
        }
        if (error) {
          setSnack({ message: `There was an error creating link: \n${error.message}`, type: 'error', open: true });
          setIsSubmitting(false);
        }
      }
    }
  };
  const invalidFormData = () => {
    return (
      (formData.setCommissionStatus && !formData.commissionStatus) ||
      (formData.setActualFirstRun && !formData.actualFirstRun) ||
      (formData.setRepAssign && (!formData.repAssignName || !formData.repAssignId)) ||
      (formData.setParentCommissionStatus && !formData.parentCommissionStatus) ||
      (formData.setActualDecommissionRun && !formData.actualDecommissionRun) ||
      (isBulkRPM && rpmAccountIds.length === 0) ||
      (isBulkRPM && rpmAccountIds.length > 1000)
    );
  };
  const disabledSaveButton = !newSfdcUpstackId || !(newRpmAccountId || isBulkRPM) || isSubmitting || invalidFormData();

  return (
    <Dialog
      data-testid="new-link-dialog"
      maxWidth="md"
      open={open}
      onClose={handleClose}
      fullWidth
      slotProps={{
        paper: { sx: { overflowY: 'visible' } }
      }}>
      <DialogTitle>
        <TextBlock
          weight="bold"
          size="lg"
          color="grey9">
          {showBulkRPMResults ? 'New Link Result' : 'New Link'}
        </TextBlock>
      </DialogTitle>
      <DialogContent
        sx={{
          minHeight: 70,
          display: 'flex',
          overflowY: 'visible',
          flexFlow: 'row wrap',
          paddingLeft: 6,
          paddingRight: 6
        }}>
        <div className="flex align-center justify-center w-full">
          <div className="space-y-3 justify-center w-1/2 mr-3">
            <TextBlock color="grey6">{startCase(toLower(typeAsset))}</TextBlock>
            {!asset?.id && !isSubmitting && (
              <SearchBox
                handleSelect={handleSelect}
                searchEntityType={typeAsset}
                handleClearSelect={handleClearSelect}
              />
            )}
            {!asset?.id && isSubmitting && (
              <TextSpan
                color="grey6"
                className="flex">
                Loading...
              </TextSpan>
            )}
            {!!asset?.id && (
              <div className="flex gap-1">
                <Icon
                  className="p-2"
                  type={iconAsset}
                />
                {asset.supplierName || ''}: {'number' in asset ? asset.number : asset.assetNumber}
              </div>
            )}
          </div>
          <div className="space-y-3 justify-center w-1/2">
            <TextBlock color="grey6">{showBulkRPMResults ? 'RPM Account List' : 'RPM Account'}</TextBlock>
            {showBulkRPMResults && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span>{rpmAccountIds.length} RPM Account IDs</span>
                  <Tooltip
                    title="Download csv"
                    arrow>
                    <button
                      className="text-blue-600 text-sm"
                      onClick={() => downloadBulkRPMLinkCreateResultsCsv(bulkRPMLinkResults)}>
                      <FileDownloadIcon fontSize="small" />
                    </button>
                  </Tooltip>
                </div>
                <div className="flex items-center gap-2">
                  <span style={{ minWidth: '25px' }}>
                    <Icon type="indigoCheckmark" />
                  </span>
                  <span>{bulkRPMLinkResults?.success.length || 0} new links</span>
                </div>
                <div className="flex items-center gap-2">
                  <span style={{ width: '25px' }}></span>
                  <span>{bulkRPMLinkResults?.activated.length || 0} existing links activated</span>
                </div>
                <div className="flex items-center gap-2">
                  <span style={{ minWidth: '25px' }}></span>
                  <span>{bulkRPMLinkResults?.duplicate.length || 0} active links ignored</span>
                </div>
                <div className="flex items-center gap-2">
                  <span style={{ minWidth: '25px' }}>
                    <Icon type="alert" />
                  </span>
                  <span>{bulkRPMLinkResults?.missingRpmAccount.length || 0} IDs not found</span>
                </div>
              </div>
            )}
            {!showBulkRPMResults && (
              <div className="flex flex-row items-center gap-3">
                {!rpmAccount?.accountId && !isSubmitting && !isBulkRPM && (
                  <SearchBox
                    handleSelect={handleSelect}
                    searchEntityType={EntityType.ACCOUNT}
                    handleClearSelect={handleClearSelect}
                  />
                )}
                {!rpmAccount?.accountId && !isSubmitting && isBulkRPM && (
                  <TextField
                    error={rpmAccountIds.length > 1000}
                    label={rpmAccountIds.length > 1000 ? 'Maximum 1,000 IDs allowed' : ''}
                    multiline
                    rows={4}
                    className="w-full border rounded p-2 text-sm"
                    placeholder="RPM Account Ids, one per line, maximum 1,000."
                    disabled={isSubmitting}
                    value={rpmAccountIds.join('\n')}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Only allow numbers and newlines
                      const filteredValue = value.replace(/[^0-9\n]/g, '');

                      if (filteredValue.length === 0) {
                        setRpmAccountIds([]);
                      } else {
                        // Split by newlines and filter out empty strings
                        const ids = filteredValue.split('\n');
                        if (!isEqual(ids, rpmAccountIds)) {
                          setRpmAccountIds(ids);
                        }
                      }
                    }}
                  />
                )}
                {!rpmAccount?.accountId && isSubmitting && (
                  <TextSpan
                    color="grey6"
                    className="flex">
                    Loading...
                  </TextSpan>
                )}
                {!rpmAccount?.accountId && !isBulkRPM && (
                  <Button
                    variant="outlined"
                    onClick={() => setIsBulkRPM(true)}
                    disabled={isSubmitting}>
                    By List
                  </Button>
                )}
                {!!rpmAccount?.accountId && (
                  <div className="flex gap-1 items-center">
                    <Icon
                      className="p-3"
                      type="rpmAccountMed"
                    />
                    {rpmAccount.supplier}: {rpmAccount.accountNumber}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {!showBulkRPMResults && (
          <>
            <hr className="my-4 -mx-6 w-[200%] border-t border-grey-3" />
            <div className="flex w-full">
              <div className="space-y-3 justify-center w-1/2 mr-3">
                <div className="justify-center w-full mb-3">
                  <FormControlLabel
                    disabled={isSubmitting || !assetData}
                    control={
                      <Checkbox
                        checked={formData.setCommissionStatus}
                        onChange={() =>
                          setFormData({ ...formData, setCommissionStatus: !formData.setCommissionStatus })
                        }
                      />
                    }
                    className="!mr-0"
                    label={
                      <TextSpan
                        color="grey6"
                        size="sm14">
                        Set Commission Status from
                        <TextSpan
                          color="grey9"
                          weight="bold"
                          size="sm14"
                          className="mx-1">
                          {assetData?.id
                            ? assetData[
                                (isAssetType ? 'commissionsStatus' : 'commissionStatus') as keyof typeof assetData
                              ]
                            : 'None'}
                        </TextSpan>
                        to
                      </TextSpan>
                    }
                  />
                  <FormControl
                    sx={{ minHeight: '1rem', lineHeight: 1, display: 'block' }}
                    fullWidth>
                    <Select
                      id="edit-component-select"
                      size="small"
                      data-testid="edit-component-select"
                      value={formData.commissionStatus}
                      disabled={!formData.setCommissionStatus || isSubmitting || !assetData}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData({ ...formData, commissionStatus: val });
                      }}>
                      {assetCommissionsStatusOptions.map((op) => (
                        <MenuItem
                          key={op}
                          value={op}>
                          {op || '--None--'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                <div className="justify-center w-full mb-3">
                  <FormControlLabel
                    disabled={isSubmitting || !assetData}
                    control={
                      <Checkbox
                        checked={formData.setActualFirstRun}
                        onChange={() => setFormData({ ...formData, setActualFirstRun: !formData.setActualFirstRun })}
                      />
                    }
                    className="!mr-0"
                    label={
                      <TextSpan
                        color="grey6"
                        size="sm14">
                        Set Actual First Run from
                        <TextSpan
                          color="grey9"
                          weight="bold"
                          size="sm14"
                          className="mx-1">
                          {getFormattedDate(assetData?.actualFirstCommissionRun, 'MMM YYYY', 'None')}
                        </TextSpan>
                        to
                      </TextSpan>
                    }
                  />
                  <DatePicker
                    closeOnSelect={false}
                    disabled={isSubmitting || !formData.setActualFirstRun || !assetData}
                    data-testid="decommission-date-picker"
                    className="w-40"
                    sx={{ minHeight: '1rem', lineHeight: 1, display: 'block' }}
                    slots={{
                      actionBar: CustomActionBar
                    }}
                    slotProps={{
                      actionBar: {
                        actions: ['next', 'accept', 'cancel']
                      },
                      textField: {
                        size: 'small'
                      }
                    }}
                    views={['month', 'year']}
                    value={formData.actualFirstRun}
                    onChange={(v) => setFormData({ ...formData, actualFirstRun: v })}
                    format={'MMM yyyy'}
                  />
                </div>
                <div
                  className="justify-center"
                  key={rpmAccountData?.accountId}>
                  <FormControlLabel
                    disabled={isSubmitting || isBulkRPM || !rpmAccountData}
                    control={
                      <Checkbox
                        checked={formData.setRepAssign}
                        onChange={() => setFormData({ ...formData, setRepAssign: !formData.setRepAssign })}
                      />
                    }
                    className="!mr-0"
                    label={
                      isBulkRPM ? (
                        <TextSpan
                          color="grey6"
                          size="sm14">
                          Set RPM Account Rep Assignment to
                        </TextSpan>
                      ) : (
                        <TextSpan
                          color="grey6"
                          size="sm14">
                          Set RPM Account Rep Assignment from
                          <TextSpan
                            color="grey9"
                            weight="bold"
                            size="sm14"
                            className="mx-1">
                            {rpmAccountData?.assignedRep
                              ? `${rpmAccountData.assignedRep}, ${rpmAccountData.assignedAgency}`
                              : 'None'}
                          </TextSpan>
                          to
                        </TextSpan>
                      )
                    }
                  />
                  <div className="w-[300px]">
                    <SearchBox
                      handleSelect={(result: SearchResult) => {
                        setFormData({
                          ...formData,
                          repAssignName: result.matchedValue,
                          repAssignId: Number(result.id)
                        });
                      }}
                      handleClearSelect={() => {
                        setFormData({ ...formData, repAssignName: '', repAssignId: null });
                      }}
                      searchEntityType={EntityType.RPM_REP}
                      inputString=""
                      fullWidth
                      placeholder="Search Rep..."
                      disabled={!formData.setRepAssign || isSubmitting || !rpmAccountData}
                    />
                  </div>
                </div>
              </div>
              {commissionRunInfo && (
                <div className="space-y-3 justify-center w-1/2">
                  <TextBlock
                    color="grey6"
                    size="sm14">
                    First Commission Run
                  </TextBlock>
                  <TextSpan
                    color="black"
                    size="sm14">
                    {getFormattedDate(commissionRunInfo.firstCommissionRun, 'MMM YYYY', 'None')}
                  </TextSpan>
                  <TextBlock
                    color="grey6"
                    className="!mt-4"
                    size="sm14">
                    Last Commission Run
                  </TextBlock>
                  <TextSpan
                    color="black"
                    size="sm14">
                    {getFormattedDate(commissionRunInfo.lastCommissionRun, 'MMM YYYY', 'None')}
                  </TextSpan>
                </div>
              )}
            </div>
            {assetParent && (
              <>
                <hr className="my-6 -mx-6 w-[200%] border-t border-grey-3" />
                <TextBlock
                  color="black"
                  size="lg">
                  Decommission Parent Asset
                </TextBlock>
                <div className="justify-center w-full mb-3 mt-3">
                  <Text
                    dataTestId="replacement-of-label-field"
                    size="sm14"
                    color="grey6">
                    Replacement Of
                  </Text>
                  <div className="flex justify-start">
                    <RecordLink to={`/asset/${assetParent.id}`}>
                      <div className="flex items-center text-small">{assetParent.number}</div>
                    </RecordLink>
                    <Tooltip
                      arrow
                      title="Open in SF"
                      placement="bottom">
                      <a
                        data-testid="openInSalesforceButton"
                        href={`${salesforceAssetUrl}/${assetParent.sf_id}/view `}
                        target="_blank"
                        rel="noreferrer">
                        <Icon type="external" />
                      </a>
                    </Tooltip>
                  </div>
                </div>
                <div className="justify-center w-full mb-3">
                  <FormControlLabel
                    disabled={isSubmitting}
                    control={
                      <Checkbox
                        checked={formData.setParentCommissionStatus}
                        onChange={() =>
                          setFormData({ ...formData, setParentCommissionStatus: !formData.setParentCommissionStatus })
                        }
                      />
                    }
                    label={
                      <TextSpan
                        color="grey6"
                        size="sm14">
                        Set Commission Status from
                        <TextSpan
                          color="grey9"
                          weight="bold"
                          size="sm14"
                          className="mx-1">
                          {assetParent.commissionsStatus || 'None'}
                        </TextSpan>
                        to
                      </TextSpan>
                    }
                  />
                  <FormControl
                    sx={{ minHeight: '1rem', lineHeight: 1, display: 'block' }}
                    fullWidth>
                    <Select
                      id="edit-component-select"
                      size="small"
                      data-testid="edit-component-select"
                      value={formData.parentCommissionStatus}
                      disabled={!formData.setParentCommissionStatus || isSubmitting}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData({ ...formData, parentCommissionStatus: val });
                      }}>
                      {assetCommissionsStatusOptions.map((op) => (
                        <MenuItem
                          key={op}
                          value={op}>
                          {op || '--None--'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                <div className="justify-center">
                  <FormControlLabel
                    disabled={isSubmitting || !assetData}
                    control={
                      <Checkbox
                        checked={formData.setActualDecommissionRun}
                        onChange={() =>
                          setFormData({ ...formData, setActualDecommissionRun: !formData.setActualDecommissionRun })
                        }
                      />
                    }
                    label={
                      <TextSpan
                        color="grey6"
                        size="sm14">
                        Set Actual Decommission Run from
                        <TextSpan
                          color="grey9"
                          weight="bold"
                          size="sm14"
                          className="mx-1">
                          {getFormattedDate(assetParent?.actualDecommissionedDate, 'MMM YYYY') || 'None'}
                        </TextSpan>
                        to
                      </TextSpan>
                    }
                  />
                  <DatePicker
                    closeOnSelect={false}
                    sx={{ minHeight: '1rem', lineHeight: 1, display: 'block' }}
                    disabled={isSubmitting || !formData.setActualDecommissionRun}
                    data-testid="decommission-date-picker"
                    className="w-40"
                    slots={{
                      actionBar: CustomActionBar
                    }}
                    slotProps={{
                      actionBar: {
                        actions: ['next', 'accept', 'cancel']
                      },
                      textField: {
                        size: 'small'
                      }
                    }}
                    views={['month', 'year']}
                    value={formData.actualDecommissionRun}
                    onChange={(v) => setFormData({ ...formData, actualDecommissionRun: v })}
                    format={'MMM yyyy'}
                  />
                </div>
              </>
            )}
          </>
        )}
      </DialogContent>
      <hr className="my-2 w-full" />
      <DialogActions sx={{ marginRight: 2, pb: 2 }}>
        <button
          className={DEFAULT_CANCEL_BUTTON_STYLE}
          onClick={handleClose}>
          {showBulkRPMResults ? 'Close' : 'Cancel'}
        </button>
        {isSubmitting ? (
          <div className="flex">
            <ComponentSpinner
              sx={{ mt: '0', m: '0' }}
              label="Creating..."
            />
          </div>
        ) : (
          <>
            {!isBulkRPM && (
              <button
                data-testid="submit-add-new-link"
                disabled={disabledSaveButton}
                onClick={() => handleSubmitForm(true)}
                className={DEFAULT_CANCEL_BUTTON_STYLE}
                type="submit">
                Save and Add Another
              </button>
            )}
            {!showBulkRPMResults && (
              <button
                data-testid="submit-new-link"
                disabled={disabledSaveButton}
                onClick={() => handleSubmitForm()}
                className={DEFAULT_ADVANCE_BUTTON_STYLE}
                type="submit">
                Save
              </button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
